import React, { useState, useEffect } from 'react';
import { Box, Button, Stack, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import PrintIcon from '@mui/icons-material/Print';

const ImageViewer = ({ imageUrl, mimeType }) => {
  const [zoom, setZoom] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state when a new image is loaded
    setZoom(1);
    setImageLoaded(false);
    setError(null);
  }, [imageUrl]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.1));
  const handleResetZoom = () => setZoom(1);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Image</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
            }
            img {
              max-width: 100%;
              max-height: 100vh;
              object-fit: contain;
            }
          </style>
        </head>
        <body>
          <img src="${imageUrl}" alt="Print Image" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setError('Failed to load image. The format may not be supported by your browser.');
    setImageLoaded(true); // Set to true to display error message
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <IconButton onClick={handleZoomOut} disabled={!imageLoaded}>
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={handleResetZoom} disabled={!imageLoaded}>
          <FitScreenIcon />
        </IconButton>
        <IconButton onClick={handleZoomIn} disabled={!imageLoaded}>
          <ZoomInIcon />
        </IconButton>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />} 
          onClick={handlePrint} 
          disabled={!imageLoaded || error}
        >
          Print
        </Button>
      </Stack>

      <Box
        sx={{
          border: '1px solid #ccc',
          height: '500px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto',
          backgroundColor: '#f5f5f5',
          position: 'relative',
        }}
      >
        {error ? (
          <Box sx={{ color: 'error.main', textAlign: 'center', p: 2 }}>
            {error}
          </Box>
        ) : (
          <img
            src={imageUrl}
            alt="Document Preview"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              transition: 'transform 0.2s ease-in-out',
            }}
          />
        )}
        {!imageLoaded && !error && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)'
            }}
          >
            Loading image...
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ImageViewer;